import { memo } from "react";
import { useTranslation } from "react-i18next";

import { EventTypeEnum } from "shared/types";

import { CountTypeEnum } from "pages/Dashboard/components/constants";
import {
  getSmartTableChartTicks,
  getYAxisKey,
  processChartDataForStackedBars,
} from "pages/Dashboard/components/utils";
import {
  X_AXIS_COLOR,
  X_AXIS_FONT_COLOR,
} from "pages/shared/Dashboard/constants";
import { formatChartDateTick } from "pages/shared/schemaDefinitions/SchemaSmartGroupsTable/utils";

import SparklineBarChart from "features/ui/charts/BarChart/SparklineBarChart";

interface SparklineChartProps {
  filteredChartData: any[];
  allTimeChartData: any[];
  eventType: EventTypeEnum;
  countType: CountTypeEnum;
  isRowHovered: boolean;
  showOnlyEventDateData: boolean;
}

export const SparklineChart = memo(
  ({
    filteredChartData,
    allTimeChartData,
    eventType,
    countType,
    isRowHovered,
    showOnlyEventDateData,
  }: SparklineChartProps) => {
    const { t } = useTranslation();
    const yAxisKey = getYAxisKey(countType, eventType);

    const { processedData, stackedBarKey } = processChartDataForStackedBars(
      showOnlyEventDateData,
      filteredChartData,
      allTimeChartData,
      yAxisKey
    );

    if (
      !processedData ||
      !Array.isArray(processedData) ||
      processedData.length === 0
    ) {
      return <div>{t("common:no_data")}</div>;
    }

    const yAxisWidth = isRowHovered ? 50 : 0;

    return (
      <div className="flex justify-end mr-12">
        <SparklineBarChart
          data={processedData}
          xAxisKey="date"
          yAxisKey={yAxisKey}
          height={50}
          width={200 + yAxisWidth}
          margin={{ top: 12, right: 15, bottom: -18, left: 15 }}
          yAxisWidth={yAxisWidth}
          yAxisProps={{ hide: !isRowHovered }}
          xAxisProps={{
            tick: { fontSize: 10, fill: X_AXIS_FONT_COLOR },
            tickLine: false,
            axisLine: { stroke: X_AXIS_COLOR },
            ticks: getSmartTableChartTicks(processedData),
            interval: 0,
            dy: -5,
            tickFormatter: formatChartDateTick,
          }}
          yAxisDomain={[0, "auto"]}
          stackedBarKey={stackedBarKey}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // custom comparison function to prevent unnecessary re-renders
    if (prevProps.isRowHovered !== nextProps.isRowHovered) return false;

    if (prevProps.showOnlyEventDateData !== nextProps.showOnlyEventDateData)
      return false;

    const prevData = prevProps.showOnlyEventDateData
      ? prevProps.filteredChartData
      : prevProps.allTimeChartData;
    const nextData = nextProps.showOnlyEventDateData
      ? nextProps.filteredChartData
      : nextProps.allTimeChartData;

    if (prevData === nextData && prevProps.eventType === nextProps.eventType)
      return true;

    if (!prevData || !nextData) return false;

    if (prevData.length !== nextData.length) return false;

    const yAxisKey =
      prevProps.eventType === EventTypeEnum.CLAIM ? "numClaims" : "numEvents";

    return prevData.every((item, index) => {
      const nextItem = nextData[index];

      return (
        item.date === nextItem.date && item[yAxisKey] === nextItem[yAxisKey]
      );
    });
  }
);
